<template>
  <TourFilterLine
    ref="filterLine"
    :is-region="props.isRegion"
  >
    <template #filters>
      <TourFilterDatesSkeleton>
        <TourFilterDates
          v-if="date"
          show-close-button
          :calendar-events="calendarEvents"
          is-fullscreen
          is-tour
          :slug
          v-bind="createSoguHandlers('date')"
        />
      </TourFilterDatesSkeleton>

      <FilterTourActivitiesSkeleton>
        <FilterTourActivities
          v-if="!isSkeletonActive"
          :is-fullscreen="isTablet"
          :activity-counts="tourActivities"
          :default-sogu-fields="props.defaultSoguFields"
          v-bind="createSoguHandlers('activity_type')"
        />
      </FilterTourActivitiesSkeleton>

      <TourFilterDurationSkeleton v-if="!isMobile">
        <TourFilterDuration
          v-if="defaultDuration"
          :duration="durationParams"
          :is-fullscreen="isTablet"
          :is-scroll="isScroll"
          class="filter-item"
          v-bind="createSoguHandlers('duration')"
        />
      </TourFilterDurationSkeleton>

      <TourFilterPriceSkeleton v-if="!isMobile">
        <TourFilterPrice
          v-if="defaultPrice"
          :currency-code="draftCurrencyCode"
          :currency-symbol="draftCurrencySymbol"
          :histogram="priceData.histogram"
          :min-price="priceData.minPrice"
          :max-price="priceData.maxPrice"
          :is-fullscreen="isTablet"
          :is-scroll="isScroll"
          :is-loading="isPriceAggregationsPending"
          class="filter-item"
          v-bind="createSoguHandlers('price')"
        />
      </TourFilterPriceSkeleton>
    </template>

    <template #modal-button>
      <TourModalFilter
        :display-currency="displayCurrencyData"
        :is-scroll="isScroll"
        :price="priceData"
        :duration-params="durationParams"
        :is-price-loading="isPriceAggregationsPending"
        v-bind="createSoguHandlers('all')"
        @display-currency-change="handleDisplayCurrencyChange"
      />
    </template>
  </TourFilterLine>
</template>

<script setup lang="ts">
  import { tourFiltersToQuery, useTourFiltersStore } from '@/entities/filter/store/tour';
  import { TourFilterDates, TourFilterDatesSkeleton } from '@/features/filter/dates';
  import { FilterTourActivities, FilterTourActivitiesSkeleton } from '@/features/filter/tour-activities';
  import { TourFilterPrice, TourFilterPriceSkeleton } from '@/features/filter/price';
  import { TourFilterDuration, TourFilterDurationSkeleton } from '@/features/filter/duration';
  import { useExperienceAggregations } from '@/entities/experience/aggregation';
  import type { Params } from '@/entities/experience/aggregation';
  import { TourFilterLine } from '@/entities/filter/filter-line';
  import TourModalFilter from './TourModalFilter.vue';
  import { useScreenSize } from '@experience/shared';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';
  import {
    type ToursSoguFilterTypes,
    useToursFilterBlockSogu,
  } from '@/widgets/filtersBlock/composables/tour/useToursFilterBlockSogu.ts';
  import { ref, computed } from 'vue';
  import type { CurrencyCode } from '@/shared/lib/currency';
  import { EXP_TYPES } from '@/entities/filter';
  import { LocationType } from '@/entities/location';
  import type { TTourFiltersPageState } from '@/entities/filter/store/tour';
  import { until } from '@vueuse/core';
  import { useCalendarEventChipsInfo } from '@/features/filter/dates/api/useFilterDates.ts';
  import { useListingSkeleton } from '@/shared/composables/useDelayedListingSkeleton.ts';
  import { syncDraftPriceFilterStateOnDisplayCurrencyChange } from '@/entities/filter/lib/clearPriceFilterOnDisplayCurrencyChange';
  import { useDisplayedCurrency } from '@/shared/composables/useDisplayedCurrency';
  import { useDisplayCurrencyFilterSogu } from '@/widgets/filtersBlock/composables/useDisplayCurrencyFilterSogu.ts';

  // Интерфейс для экспортируемых свойств FilterLine
  interface FilterLineExpose {
    isScroll: boolean;
    isOverlowFilterScroll: boolean;
    isShowGradient: boolean;
  }

  const filtersStore = useTourFiltersStore();
  const { isSkeletonActive } = useListingSkeleton();
  const { isMobile, isTablet } = useScreenSize();
  const filterLine = ref<FilterLineExpose | null>(null);

  interface Props {
    page: number;
    location: LocationType;
    slug: string;
    defaultSoguFields: ContextGeoSubject;
    currencyCode: CurrencyCode;
    isRegion: boolean;
  }

  const props = defineProps<Props>();
  const { draftIso, draftCurrencyCode, draftCurrencySymbol, selectDraftCurrency, displayCurrencies } =
    useDisplayedCurrency(() => props.currencyCode);

  const { trackDisplayCurrencyFilterSelect } = useDisplayCurrencyFilterSogu();

  const displayCurrencyData = computed(() => ({
    code: draftCurrencyCode.value,
    symbol: draftCurrencySymbol.value,
    iso: draftIso.value,
    currencies: displayCurrencies.value,
  }));

  const handleDisplayCurrencyChange = (isoCode: string) => {
    const previousCurrency = draftIso.value;

    if (!selectDraftCurrency(isoCode)) {
      return;
    }

    trackDisplayCurrencyFilterSelect(previousCurrency ?? '', isoCode);
    syncDraftPriceFilterStateOnDisplayCurrencyChange(isoCode, filtersStore.state, filtersStore.searchState);
  };

  const emit = defineEmits<{
    updated: [];
  }>();

  const { calendarEvents } = useCalendarEventChipsInfo(computed(() => true));

  const isScroll = computed(() => filterLine.value?.isScroll || false);

  const durationParams = computed(() => defaultDuration.value || {});

  const aggregationParams = computed<Params>((): Params => {
    const pageType = props.location === LocationType.COUNTRY ? 'tours-country' : props.page;

    const pageParams: TTourFiltersPageState = {
      city: props.location === LocationType.CITY ? props.slug : undefined,
      region: props.location === LocationType.REGION ? props.slug : undefined,
      country: props.location === LocationType.COUNTRY ? props.slug : undefined,
    };

    const queryParams = tourFiltersToQuery(filtersStore.searchState, pageParams);

    return {
      ...queryParams,
      page: pageType,
      date_filter_mode: queryParams?.start_date || queryParams?.end_date ? 'event_date_range' : undefined,
      bookable: true,
      persons_count: queryParams.persons_count || 1,
      type: queryParams.type || EXP_TYPES.TOUR,
      extended_movement_types: true,
    } as Params;
  });

  const defaultAggregationParams = computed<Params>(() => ({
    city__slug: props.location === LocationType.CITY ? props.slug : null,
    country__slug: props.location === LocationType.COUNTRY ? props.slug : null,
    region__slug: props.location === LocationType.REGION ? props.slug : null,
    type: EXP_TYPES.TOUR,
    persons_count: 1,
    page: props.page,
  }));

  const { duration: defaultDuration, isExperienceAggregationsPending: isDurationAggregationsPending } =
    useExperienceAggregations(defaultAggregationParams, { withDisplayCurrency: false });

  const { price: defaultPrice, isExperienceAggregationsPending: isPriceAggregationsPending } =
    useExperienceAggregations(defaultAggregationParams, {
      displayCurrencyIso: draftIso,
    });

  const {
    date,
    tourActivities,
    isExperienceAggregationsPending: isDateAggregationsPending,
  } = useExperienceAggregations(aggregationParams, { withDisplayCurrency: false });

  const filtersScroll = async () => {
    await until(isDurationAggregationsPending).toBe(false);
    await until(isPriceAggregationsPending).toBe(false);
    await until(isDateAggregationsPending).toBe(false);
    emit('updated');
  };

  const priceData = computed(() => ({
    histogram: defaultPrice.value?.defaultHistogram as Record<string, number>,
    minPrice: defaultPrice.value?.defaultRange?.[0] as number,
    maxPrice: defaultPrice.value?.defaultRange?.[1] as number,
  }));

  const { soguFilterApplyEvent, soguFilterOpenEvent, soguFilterResetEvent } = useToursFilterBlockSogu(
    computed(() => props.location),
  );

  const createSoguHandlers = (type: ToursSoguFilterTypes) => ({
    onOpen: () => soguFilterOpenEvent(props.defaultSoguFields, type),
    onApply: () => {
      soguFilterApplyEvent(props.defaultSoguFields, type);
      filtersScroll();
    },
    onReset: () => soguFilterResetEvent(props.defaultSoguFields, type),
  });
</script>

<style lang="scss" scoped>
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .filter-item {
    @include adaptivity.mobile {
      display: none;
    }
  }
</style>
